import { inject, Injectable } from '@angular/core';
import { normalize, sanitize } from '@app/shared';
import { ApiService } from '..';
import { CepInterface } from './cep.interface';

const VIACEP_BASE_URL = 'https://viacep.com.br/ws';

interface ViaCepRawResponse extends CepInterface {
  erro?: boolean;
}

/**
 * Serviço de consulta de CEP via ViaCEP.
 * Requisições passam pelo {@link ApiService} (gateway unificado).
 * URLs absolutas são ignoradas pelo interceptor de auth — sem Bearer token em APIs externas.
 */
@Injectable({ providedIn: 'root' })
export class CepService {
  private readonly apiService = inject(ApiService);

  public async searchCep(cep: string): Promise<CepInterface> {
    const sanitizedCep: string = sanitize(cep).replace(/\D/g, '');
    if (sanitizedCep.length !== 8) {
      throw new Error('CEP inválido');
    }
    const path: string = `${VIACEP_BASE_URL}/${sanitizedCep}/json/`;
    const response: ViaCepRawResponse = await this.apiService.get<ViaCepRawResponse>({ path });
    if (response.erro) {
      throw new Error('CEP não encontrado');
    }
    return this.mapResponse(response);
  }

  private mapResponse(response: ViaCepRawResponse): CepInterface {
    return {
      cep: response.cep,
      logradouro: normalize((response.logradouro ?? '').toUpperCase()),
      complemento: normalize((response.complemento ?? '').toUpperCase()),
      bairro: normalize((response.bairro ?? '').toUpperCase()),
      localidade: normalize((response.localidade ?? '').toUpperCase()),
      uf: normalize((response.uf ?? '').toUpperCase()),
      ddd: response.ddd,
      siafi: response.siafi,
      ibge: response.ibge,
      gia: response.gia,
    };
  }
}
